import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as reviewsApi from '../api/reviewsApi';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
  TableSkeleton,
} from '../components/ui';
import { IconX } from '../components/icons';
import { Review, ReviewStats, ReviewStatus } from '../types/review';
import { formatDateTime } from '../utils/format';

const PAGE_SIZE = 20;

const EMPTY_STATS: ReviewStats = { pending: 0, approved: 0, rejected: 0, total: 0 };

type Tab = ReviewStatus | 'all';

const TABS: { key: Tab; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

/** The action a confirmation dialog is currently holding. */
type PendingAction = { kind: 'approve' | 'reject' | 'delete'; review: Review } | null;

function StatusBadge({ status }: { status: ReviewStatus }) {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
        Approved
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center rounded-full border border-bad/40 bg-bad/10 px-2.5 py-0.5 text-xs font-medium text-bad">
        Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
      Pending
    </span>
  );
}

function StarRow({ rating }: { rating: number }) {
  return (
    <span className="whitespace-nowrap text-gold" title={`${rating} of 5`}>
      {'★'.repeat(rating)}
      <span className="text-sub/40">{'☆'.repeat(5 - rating)}</span>
    </span>
  );
}

/** Review body: clamped to two lines until clicked, then full text. */
function ReviewBody({ review }: { review: Review }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="max-w-md text-left"
      title={open ? 'Click to collapse' : 'Click to expand'}
    >
      {review.title && <p className="text-sm font-medium">{review.title}</p>}
      <p className={`text-sm text-sub ${open ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}>
        {review.body}
      </p>
    </button>
  );
}

export default function ReviewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep link from the Products page: "show me the reviews for this product".
  const productId = searchParams.get('product_id') || '';

  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats>(EMPTY_STATS);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState<Tab>('pending');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const [action, setAction] = useState<PendingAction>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reviewsApi.getReviews({
        status: tab,
        productId: productId || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setReviews(res.reviews);
      setStats(res.stats);
      setTotal(res.total);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tab, productId, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [tab, productId, from, to]);

  async function runAction() {
    if (!action) return;
    setBusy(true);
    setError('');
    try {
      if (action.kind === 'approve') await reviewsApi.approveReview(action.review.id);
      if (action.kind === 'reject') await reviewsApi.rejectReview(action.review.id);
      if (action.kind === 'delete') await reviewsApi.deleteReview(action.review.id);
      setAction(null);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err));
      setAction(null);
    } finally {
      setBusy(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const productName = productId ? reviews[0]?.product_name : null;

  const confirmCopy: Record<'approve' | 'reject' | 'delete', { title: string; message: string; label: string }> = {
    approve: {
      title: 'Approve this review?',
      message: 'It will be visible to all customers immediately, and the product rating will be recalculated.',
      label: 'Approve',
    },
    reject: {
      title: 'Reject this review?',
      message:
        'It will be hidden from customers. If it was previously approved, its rating is removed from the product average.',
      label: 'Reject',
    },
    delete: {
      title: 'Delete this review permanently?',
      message: 'This cannot be undone — use it for spam or abuse. Rejecting keeps the record instead.',
      label: 'Delete',
    },
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Reviews</h1>
        <p className="text-sm text-sub">
          Customer reviews from verified purchases. Nothing is visible on the storefront until approved.
        </p>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* Summary */}
      {loading && reviews.length === 0 ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Awaiting moderation</p>
            <p className="mt-2 text-2xl font-semibold text-warn">{stats.pending}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Approved</p>
            <p className="mt-2 text-2xl font-semibold text-ok">{stats.approved}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Rejected</p>
            <p className="mt-2 text-2xl font-semibold text-sub">{stats.rejected}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Total</p>
            <p className="mt-2 text-2xl font-semibold">{stats.total}</p>
          </div>
        </div>
      )}

      {/* Product filter chip — only when arriving from a product row */}
      {productId && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-medium text-gold">
            Filtered to {productName || 'one product'}
            <button
              onClick={() => setSearchParams({}, { replace: true })}
              aria-label="Clear product filter"
              className="hover:text-ink"
            >
              <IconX className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      {/* Tabs + date range */}
      <div className="card flex flex-wrap items-end justify-between gap-4 p-4">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => {
            const count =
              t.key === 'all' ? stats.total : stats[t.key as 'pending' | 'approved' | 'rejected'];
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${
                  tab === t.key ? 'bg-gold/10 text-gold' : 'text-sub hover:bg-edge/40 hover:text-ink'
                }`}
              >
                {t.label}
                <span className="ml-1.5 text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">From</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {(from || to) && (
            <button
              className="btn-ghost mb-0.5"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
            >
              Clear dates
            </button>
          )}
        </div>
      </div>

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={8} />
        ) : reviews.length === 0 ? (
          <EmptyState
            message={tab === 'pending' ? 'Nothing awaiting moderation' : 'No reviews found'}
            hint={
              tab === 'pending'
                ? 'New reviews from verified purchasers land here.'
                : 'Try a different tab or date range.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Product</th>
                    <th className="th">Customer</th>
                    <th className="th">Rating</th>
                    <th className="th">Review</th>
                    <th className="th">Submitted</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {reviews.map((r) => (
                    <tr key={r.id} className="align-top transition-colors hover:bg-edge/20">
                      <td className="td font-medium">{r.product_name || <span className="text-sub">—</span>}</td>
                      <td className="td">
                        <p className="font-medium">{r.user_name}</p>
                        <p className="text-xs text-sub" title={`User ${r.user_id} · Order ${r.order_id}`}>
                          {r.user_id.slice(0, 8)}…
                        </p>
                      </td>
                      <td className="td">
                        <StarRow rating={r.rating} />
                      </td>
                      <td className="td">
                        <ReviewBody review={r} />
                      </td>
                      <td className="td whitespace-nowrap text-sub">
                        <p>{formatDateTime(r.created_at)}</p>
                        <div className="mt-1">
                          <StatusBadge status={r.status} />
                        </div>
                      </td>
                      <td className="td">
                        <div className="flex flex-wrap gap-2">
                          {r.status !== 'approved' && (
                            <button
                              className="btn-gold !px-3 !py-1.5 !text-xs"
                              onClick={() => setAction({ kind: 'approve', review: r })}
                            >
                              Approve ✓
                            </button>
                          )}
                          {r.status !== 'rejected' && (
                            <button
                              className="btn-outline !px-3 !py-1.5 !text-xs"
                              onClick={() => setAction({ kind: 'reject', review: r })}
                            >
                              Reject ✗
                            </button>
                          )}
                          <button
                            className="btn-danger !px-3 !py-1.5 !text-xs"
                            onClick={() => setAction({ kind: 'delete', review: r })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {action && (
        <ConfirmDialog
          open
          title={confirmCopy[action.kind].title}
          message={
            <>
              <p>{confirmCopy[action.kind].message}</p>
              <p className="mt-3 rounded-lg border border-edge bg-edge/20 p-3 text-xs">
                <span className="font-medium">{action.review.user_name}</span> on{' '}
                <span className="font-medium">{action.review.product_name || 'this product'}</span> —{' '}
                {action.review.rating}★
              </p>
            </>
          }
          confirmLabel={confirmCopy[action.kind].label}
          danger={action.kind !== 'approve'}
          busy={busy}
          onConfirm={runAction}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
